import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getDuePayments,
  startChargeCheckout,
  getAttemptState,
  formatMoney,
  type DueCharge,
  type DueClub,
} from "../../services/payments";
import { useFeatures } from "../../hooks/useFeatures";
import AutoPayCard from "./AutoPayCard";

/**
 * What a parent owes, and the button that settles it.
 *
 * Everything this screen does is in service of one number being right and one
 * tap being enough. A parent opens the app because somebody said there is
 * money to pay; if they then have to work out which of four lines is this
 * month's, the app has failed at the only job it had.
 *
 * Grouped by club because the payment is, unavoidably: the money is created on
 * the club's own Stripe account, so a basket cannot span two clubs. Everything
 * for one club, including two children, goes in one payment.
 */

/** How long to keep asking whether the webhook has landed. */
const POLL_MS = 1500;
const POLL_ATTEMPTS = 12;

export default function PayDueCard({ onPaid }: { onPaid?: () => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { features } = useFeatures();

  const [charges, setCharges] = useState<DueCharge[]>([]);
  const [clubs, setClubs] = useState<DueClub[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saveCard, setSaveCard] = useState(false);
  const [busyClub, setBusyClub] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getDuePayments();
      setCharges(data.charges);
      setClubs(data.clubs);
      // Everything pre-selected: paying all of it is what a parent came to do,
      // and un-ticking is easier than hunting for the right row to tick.
      //
      // Except a slice of a payment plan that is not due yet. Paying ahead is
      // allowed, but a family on a plan asked to spread the cost — assuming
      // they want to pay the next two now would undo the arrangement.
      setSelected(
        new Set(
          data.charges.flatMap((c) =>
            c.instalments.length === 0
              ? [c.id]
              : c.instalments
                  .filter((i) => i.outstanding_cents > 0 && (i.days_until_due ?? 0) <= 0)
                  .map((i) => i.id)
          )
        )
      );
    } catch {
      setCharges([]);
      setClubs([]);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Coming back from Stripe.
   *
   * The redirect and the webhook race, and the redirect usually wins — so this
   * polls the attempt rather than believing the success URL. A success URL
   * proves nothing: anyone can open one.
   */
  const handledAttempt = useRef<string | null>(null);
  useEffect(() => {
    const outcome = searchParams.get("payment");
    const attemptId = searchParams.get("attempt");
    if (!outcome || !attemptId || handledAttempt.current === attemptId) return;
    handledAttempt.current = attemptId;

    const clear = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("payment");
      next.delete("attempt");
      setSearchParams(next, { replace: true });
    };

    if (outcome === "cancel") {
      toast("Plata a fost anulată. Nu s-a reținut nimic.", { icon: "ℹ️" });
      clear();
      return;
    }

    let cancelled = false;
    setConfirming(true);
    (async () => {
      for (let i = 0; i < POLL_ATTEMPTS && !cancelled; i++) {
        try {
          const state = await getAttemptState(attemptId);
          if (state.status === "succeeded") {
            toast.success(`Plată confirmată — ${formatMoney(state.amount_cents, state.currency)}. Mulțumim!`);
            await load();
            onPaid?.();
            break;
          }
          if (state.status === "failed" || state.status === "expired" || state.status === "canceled") {
            toast.error(state.failure_message || "Plata nu a reușit. Poți încerca din nou.");
            break;
          }
        } catch {
          break;
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
      }
      if (!cancelled) {
        setConfirming(false);
        clear();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const byClub = useMemo(() => {
    const groups = new Map<string, { club: DueClub; rows: DueCharge[] }>();
    for (const c of charges) {
      const club =
        clubs.find((x) => x.club_id === c.club_id) || {
          club_id: c.club_id,
          club_name: c.club_name,
          can_pay_online: false,
          blocked_reason: null,
          blocked_message: null,
        };
      const g = groups.get(c.club_id) || { club, rows: [] };
      g.rows.push(c);
      groups.set(c.club_id, g);
    }
    return Array.from(groups.values());
  }, [charges, clubs]);

  /**
   * One payable line. A period with no plan is one line; a period on a plan is
   * one line per slice, and the lump is never offered — which is the whole
   * point of the plan.
   */
  type PayItem = {
    key: string;
    charge: DueCharge;
    instalmentId: string | null;
    label: string | null;
    outstanding_cents: number;
    days_until_due: number;
  };

  const itemsOf = (rows: DueCharge[]): PayItem[] =>
    rows.flatMap((c): PayItem[] =>
      c.instalments.length === 0
        ? [
            {
              key: c.id,
              charge: c,
              instalmentId: null,
              label: null,
              outstanding_cents: c.outstanding_cents,
              days_until_due: c.days_until_due,
            },
          ]
        : c.instalments
            .filter((i) => i.outstanding_cents > 0)
            .map((i) => ({
              key: i.id,
              charge: c,
              instalmentId: i.id,
              label: `Rata ${i.seq} din ${i.count}`,
              outstanding_cents: i.outstanding_cents,
              days_until_due: i.days_until_due ?? 0,
            }))
    );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedTotalFor = (items: PayItem[]) =>
    items.filter((i) => selected.has(i.key)).reduce((sum, i) => sum + i.outstanding_cents, 0);

  const pay = async (clubId: string, items: PayItem[]) => {
    const picked = items.filter((i) => selected.has(i.key));
    if (picked.length === 0) {
      toast.error("Selectează cel puțin o plată.");
      return;
    }
    // Slices go up as slices; whole periods as periods. The server derives the
    // periods the slices belong to, so they are not sent twice.
    const instalmentIds = picked.filter((i) => i.instalmentId).map((i) => i.instalmentId!);
    const chargeIds = picked.filter((i) => !i.instalmentId).map((i) => i.charge.id);

    setBusyClub(clubId);
    try {
      const { url } = await startChargeCheckout({
        chargeIds,
        instalmentIds,
        saveCard,
        returnPath: window.location.pathname,
      });
      // Same pattern as the rest of the app's Stripe hand-offs.
      window.location.href = url;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut deschide plata. Încearcă din nou.");
      setBusyClub(null);
    }
  };

  // The module is not live: no amounts, no buttons, nothing. A parent should
  // not learn that in-app payment exists before the platform is ready for it.
  if (!features.paymentsOnline) return null;

  if (!loaded) return null;

  if (confirming) {
    return (
      <div className="p-5 bg-white border border-indigo-200 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
          <p className="text-sm font-semibold text-slate-700">Confirmăm plata...</p>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Durează câteva secunde. Poți închide ecranul — plata se înregistrează oricum.
        </p>
      </div>
    );
  }

  if (charges.length === 0) {
    return (
      <div className="space-y-4">
        <div className="p-5 text-center bg-white border border-emerald-200 rounded-2xl">
          <p className="text-sm font-bold text-emerald-700">Nu ai nimic de plată.</p>
          <p className="mt-1 text-xs text-slate-500">Toate cotizațiile sunt la zi.</p>
        </div>
        {/* Still shown: a parent with nothing due is exactly who wants to turn
            automatic payment on, and the saved cards live here. */}
        <AutoPayCard clubs={clubs} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {byClub.map(({ club, rows }) => {
        const items = itemsOf(rows);
        const total = selectedTotalFor(items);
        const overdue = items.some((i) => i.days_until_due < 0);

        return (
          <div
            key={club.club_id}
            className={`bg-white rounded-2xl border overflow-hidden ${
              overdue ? "border-rose-200" : "border-slate-200"
            }`}
          >
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-extrabold text-slate-800">{club.club_name}</p>
              {overdue && (
                <p className="text-xs font-semibold text-rose-600">Ai o plată depășită de termen</p>
              )}
            </div>

            <div className="divide-y divide-slate-100">
              {items.map((item) => {
                const r = item.charge;
                return (
                  <label key={item.key} className="flex items-start gap-3 px-4 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(item.key)}
                      onChange={() => toggle(item.key)}
                      className="mt-1 w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-slate-800">{r.product_name}</span>
                      <span className="block text-xs text-slate-500">
                        {r.period_label}
                        {item.label ? ` · ${item.label}` : ""}
                        {r.athlete_name ? ` · ${r.athlete_name}` : ""}
                      </span>
                      <span
                        className={`block mt-0.5 text-xs font-semibold ${
                          item.days_until_due < 0
                            ? "text-rose-600"
                            : item.days_until_due <= 3
                              ? "text-amber-600"
                              : "text-slate-400"
                        }`}
                      >
                        {dueLabel(item.days_until_due)}
                      </span>
                      {/* The discount belongs to the period, so it is said once,
                          on the first slice rather than on every one. */}
                      {r.discount_cents > 0 && r.discount_note && (!item.label || item.label.startsWith("Rata 1")) && (
                        <span className="block mt-0.5 text-xs font-semibold text-emerald-700">
                          {r.discount_note} · −{formatMoney(r.discount_cents, r.currency)}
                        </span>
                      )}
                    </span>
                    <span className="text-right">
                      {r.discount_cents > 0 && !item.label && (
                        <span className="block text-[11px] line-through text-slate-400 tabular-nums">
                          {formatMoney(r.amount_cents, r.currency)}
                        </span>
                      )}
                      <span className="block text-sm font-extrabold tabular-nums text-slate-800">
                        {formatMoney(item.outstanding_cents, r.currency)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="px-4 py-4 bg-slate-50">
              {club.can_pay_online ? (
                <>
                  <button
                    type="button"
                    onClick={() => pay(club.club_id, items)}
                    disabled={busyClub !== null || total === 0}
                    className="w-full px-4 py-3 text-sm font-extrabold text-white transition-colors bg-indigo-600 rounded-2xl hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {busyClub === club.club_id
                      ? "Se deschide plata..."
                      : `Plătește ${formatMoney(total, rows[0]?.currency || "RON")}`}
                  </button>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveCard}
                      onChange={(e) => setSaveCard(e.target.checked)}
                      className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="text-xs text-slate-600">
                      Salvează cardul pentru {club.club_name}, ca să nu îl mai introduci
                    </span>
                  </label>
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                    Plata merge direct la club. Datele cardului le procesează Stripe — nu trec prin
                    aplicație.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    {club.blocked_message || "Clubul nu acceptă încă plata online."}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Întreabă clubul cum vrea să încaseze — numerar sau transfer.
                  </p>
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Reuses the clubs already loaded above rather than asking again. */}
      <AutoPayCard clubs={clubs} />
    </div>
  );
}

/** The date, said the way a person would say it. */
function dueLabel(days: number): string {
  if (days < -1) return `Restant de ${Math.abs(days)} zile`;
  if (days === -1) return "Restant de ieri";
  if (days === 0) return "Scadent azi";
  if (days === 1) return "Scadent mâine";
  return `Scadent în ${days} zile`;
}
